// client/src/hooks/useSignupForm.js

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import ApiUsers from '../api/ApiUsers'
import ApiPlayers from '../api/ApiPlayers'
import ApiReferrals from '../api/ApiReferrals'
import toastr from 'toastr'
import { track } from '../analytics'
import useAutofillSync from './useAutofillSync'

// Referral code validation constants
// Max length matches database column constraint to prevent truncation
// Allows for human-readable codes while preventing abuse
const MAX_REFERRAL_CODE_LENGTH = 50
// 500ms provides good balance between responsiveness and reducing unnecessary API calls
// during rapid typing. Adjust if users report sluggish feedback or excessive API traffic.
const REFERRAL_CODE_VALIDATION_DEBOUNCE_MS = 500

/**
 * Custom hook for managing signup form state, validation, and API operations
 * Extracts all business logic from Signup component following SRP
 *
 * @returns {Object} Form state and handlers for signup operations
 */
export default function useSignupForm() {
  const navigate = useNavigate()

  // Form state
  const [userData, setUserData] = useState({
    email: '',
    invite_code: '',
    phone_e164: '',
    sms_consent_status: false,
  })

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordsMatch, setPasswordsMatch] = useState(null)
  const [creating, setCreating] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [termsError, setTermsError] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
  })
  const [errors, setErrors] = useState({})

  // Referral code state
  const [referralCode, setReferralCode] = useState('')
  const [isValidatingReferralCode, setIsValidatingReferralCode] =
    useState(false)
  /** @type {[('valid' | 'invalid' | null), Function]} */
  const [referralCodeStatus, setReferralCodeStatus] = useState(null)

  // Refs for autofill detection
  const emailRef = useRef(null)
  const inviteCodeRef = useRef(null)
  const passwordRef = useRef(null)
  const confirmPasswordRef = useRef(null)
  const referralCodeRef = useRef(null)

  // Ref for terms checkbox to avoid direct DOM manipulation
  const termsCheckboxRef = useRef(null)

  // Ref for debounced validation
  const validationTimeoutRef = useRef(null)
  const validationCounterRef = useRef(0)

  // Sync autofilled values to state
  const handleAutofillSync = useCallback((values) => {
    if (values.email || values.invite_code) {
      setUserData((prev) => ({
        ...prev,
        ...(values.email && { email: values.email }),
        ...(values.invite_code && { invite_code: values.invite_code }),
      }))
    }
    if (values.password) {
      setPassword(values.password)
    }
    if (values.confirmPassword) {
      setConfirmPassword(values.confirmPassword)
    }
  }, [])

  // Memoize refs mapping so useAutofillSync doesn't re-run every render
  const autofillRefs = useMemo(
    () => ({
      email: emailRef,
      invite_code: inviteCodeRef,
      password: passwordRef,
      confirmPassword: confirmPasswordRef,
    }),
    [],
  )

  useAutofillSync(autofillRefs, handleAutofillSync)

  // Password strength validation effect
  useEffect(() => {
    if (password.length === 0) {
      setPasswordStrength({
        isValid: false,
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
      })
      return
    }

    const hasMinLength = password.length >= 8
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumber = /[0-9]/.test(password)
    const isValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber

    setPasswordStrength({
      isValid,
      hasMinLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
    })
  }, [password])

  // Password match validation effect
  useEffect(() => {
    if (confirmPassword.length === 0) {
      setPasswordsMatch(null)
    } else {
      setPasswordsMatch(password === confirmPassword)
    }
  }, [password, confirmPassword])

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const parsedValue = type === 'checkbox' ? checked : value
    setUserData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }))

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  // Password change handlers
  const handlePasswordChange = (e) => {
    setPassword(e.target.value)

    // Clear password error when user types
    if (errors.password) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.password
        return newErrors
      })
    }
  }

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value)

    // Clear confirmPassword error when user types
    if (errors.confirmPassword) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.confirmPassword
        return newErrors
      })
    }
  }

  // Referral code validation
  const validateReferralCodeFormat = useCallback((code) => {
    if (!code.trim()) {
      return { valid: true } // Empty is okay
    }

    const trimmed = code.trim()

    if (trimmed.length > MAX_REFERRAL_CODE_LENGTH) {
      return {
        valid: false,
        error: `Referral code must be ${MAX_REFERRAL_CODE_LENGTH} characters or less`,
      }
    }

    if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
      return {
        valid: false,
        error:
          'Referral code can only contain letters, numbers, hyphens, and underscores',
      }
    }

    return { valid: true }
  }, [])

  // Debounced async validation
  const validateReferralCodeAsync = useCallback(
    async (code) => {
      // Clear any existing timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }

      // Empty code is valid (optional field)
      if (!code.trim()) {
        setReferralCodeStatus(null)
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.referralCode
          return newErrors
        })
        return
      }

      // Client-side validation first
      const formatValidation = validateReferralCodeFormat(code)
      if (!formatValidation.valid) {
        setReferralCodeStatus('invalid')
        setErrors((prev) => ({
          ...prev,
          referralCode: formatValidation.error,
        }))
        return
      }

      // Increment validation counter to track current validation
      validationCounterRef.current += 1
      const currentValidationId = validationCounterRef.current

      // Debounce API call
      validationTimeoutRef.current = setTimeout(async () => {
        setIsValidatingReferralCode(true)
        try {
          const response = await ApiReferrals.validateReferralCode(code.trim())

          // Only update state if this is still the current validation
          if (currentValidationId === validationCounterRef.current) {
            if (response.valid) {
              setReferralCodeStatus('valid')
              setErrors((prev) => {
                const newErrors = { ...prev }
                delete newErrors.referralCode
                return newErrors
              })
            } else {
              setReferralCodeStatus('invalid')
              setErrors((prev) => ({
                ...prev,
                referralCode:
                  "This referral code doesn't exist or is no longer active",
              }))
            }
          }
        } catch (error) {
          console.error('Error validating referral code:', error)
          // Only update state if this is still the current validation
          if (currentValidationId === validationCounterRef.current) {
            setReferralCodeStatus('invalid')
            setErrors((prev) => ({
              ...prev,
              referralCode: 'Unable to validate referral code',
            }))
          }
        } finally {
          // Only update loading state if this is still the current validation
          if (currentValidationId === validationCounterRef.current) {
            setIsValidatingReferralCode(false)
          }
        }
      }, REFERRAL_CODE_VALIDATION_DEBOUNCE_MS)
    },
    [validateReferralCodeFormat],
  )

  // Referral code change handler
  const handleReferralCodeChange = (e) => {
    const value = e.target.value.trim()
    setReferralCode(value)
    validateReferralCodeAsync(value)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  // Terms checkbox handler
  const handleTermsChange = (e) => {
    setAgreedToTerms(e.target.checked)
    // Clear error when user checks the box
    if (e.target.checked) {
      setTermsError(false)
      // Also clear from errors object
      if (errors.terms) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.terms
          return newErrors
        })
      }
    }
  }

  // Password visibility toggles
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  // Social authentication handlers
  const handleSocialAuthSuccess = (result) => {
    // Social auth successful
    if (result?.isNewUser) {
      navigate('/complete-profile')
    } else {
      navigate('/')
    }
  }

  const handleSocialAuthError = (error) => {
    console.error('Social auth error:', error)
  }

  // Validation function for client-side validation
  const validate = () => {
    const newErrors = {}

    // Validate email
    if (!userData.email) {
      newErrors.email = 'Email is required'
    }

    // Validate invite code
    if (!userData.invite_code) {
      newErrors.invite_code = 'Invite code is required'
    }

    // Validate password strength
    if (!passwordStrength.isValid) {
      newErrors.password =
        'Password must be at least 8 characters and contain uppercase, lowercase, and a number.'
    }

    // Validate passwords match
    if (!passwordsMatch) {
      newErrors.confirmPassword = 'Passwords do not match.'
    }

    // Validate phone format if provided
    if (userData.phone_e164) {
      if (!/^\+[1-9]\d{6,14}$/.test(userData.phone_e164.trim())) {
        newErrors.phone_e164 =
          'Enter a valid international phone number (e.g. +15551234567)'
      }
    }

    // Validate SMS consent requires a phone number
    if (userData.sms_consent_status && !userData.phone_e164) {
      newErrors.sms_consent_status =
        'A phone number is required to opt in to SMS notifications'
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      newErrors.terms =
        'You must agree to the Terms of Service and Privacy Policy.'
    }

    setErrors(newErrors)
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors }
  }

  // Main form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Run client-side validation
    const { isValid, errors: validationErrors } = validate()

    if (!isValid) {
      // Focus the first invalid field for better UX
      // Check errors in logical field order
      if (validationErrors.email && emailRef.current) {
        emailRef.current.focus()
      } else if (validationErrors.invite_code && inviteCodeRef.current) {
        inviteCodeRef.current.focus()
      } else if (validationErrors.password && passwordRef.current) {
        passwordRef.current.focus()
      } else if (
        validationErrors.confirmPassword &&
        confirmPasswordRef.current
      ) {
        confirmPasswordRef.current.focus()
      } else if (!agreedToTerms && termsCheckboxRef.current) {
        // Scroll to the terms checkbox and focus it
        termsCheckboxRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        termsCheckboxRef.current.focus()
      }
      return
    }

    setCreating(true)

    try {
      // Check if user already exists
      // Check if user already exists; invite_code validation is deferred to
      // the create step — checkExisting accepts email only.
      const exists = await ApiUsers.checkExisting(userData.email)
      if (exists) {
        setErrors({ email: 'An account with this email already exists.' })
        setCreating(false)
        // Focus email field for user to correct
        if (emailRef.current) {
          emailRef.current.focus()
        }
        return
      }

      // Create user account
      const fullUserData = {
        ...userData,
        password,
        sms_consent_status: userData.sms_consent_status ? 'granted' : 'none',
      }

      // Add referral code if provided and valid
      if (referralCode.trim() && referralCodeStatus === 'valid') {
        fullUserData.referral_code = referralCode.trim()
      }

      const response = await ApiUsers.create(fullUserData)
      const { userId, emailSent, message } = response

      // Create primary player profile
      const playerData = {
        user_id: userId,
        primary_player: true,
      }

      await ApiPlayers.create(playerData)

      // Track successful account creation
      try {
        track('user_signed_up', {
          email: userData.email,
          user_id: userId,
          timestamp: new Date().toISOString(),
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      // Show success modal for account creation
      if (emailSent) {
        setShowSuccessModal(true)
      } else {
        // If email failed to send, show warning toast and navigate
        toastr.warning(
          message ||
            'Account created but verification email failed to send. Please contact support.',
          '',
          {
            timeOut: 8000,
            closeButton: true,
            positionClass: 'toast-top-center',
          },
        )
        navigate('/verify-email-pending')
      }
    } catch (err) {
      console.error(err)
      // Show the error message from the backend
      const errorMessage = err.response?.data?.error || err.message
      toastr.error(errorMessage)
    } finally {
      setCreating(false)
    }
  }

  // Navigation handlers
  const navigateToSignIn = useCallback(() => {
    navigate('/sign-in')
  }, [navigate])

  const handleSuccessModalContinue = useCallback(() => {
    setShowSuccessModal(false)
    navigate('/')
  }, [navigate])

  // Return hook interface
  return {
    // Form state
    userData,
    password,
    confirmPassword,
    passwordsMatch,
    creating,
    agreedToTerms,
    showPassword,
    showConfirmPassword,
    passwordStrength,
    termsError,
    termsCheckboxRef,
    showSuccessModal,
    errors,

    // Referral code state
    referralCode,
    isValidatingReferralCode,
    referralCodeStatus,

    // Refs for autofill detection
    emailRef,
    inviteCodeRef,
    passwordRef,
    confirmPasswordRef,
    referralCodeRef,

    // Form handlers
    handleInputChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleTermsChange,
    handleSubmit,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleReferralCodeChange,

    // Social auth handlers
    handleSocialAuthSuccess,
    handleSocialAuthError,

    // Navigation handlers
    navigateToSignIn,
    handleSuccessModalContinue,
  }
}
